import React, { useMemo, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import type { SubmitHandler } from "react-hook-form";
import { useForm, Controller } from "react-hook-form";
import KaboodleModal from "./KaboodleModal";
import Button from "components/Button/Button";

import { TextInput } from "components/inputs";

import { useCreateRreTinMutation } from "services/rre-api";
import { useNavigate } from "react-router-dom";
import { hasApiError } from "utils/error-utils";
import Card from "components/Card/Card";

type ModalProps = {
  isOpen: boolean;
  onClose: () => void;
};

type FormData = {
  tin: string;
  tin_office_code_mailing_name: string;
  office_code: string;
};

const CreateRRETinModal: React.FC<ModalProps> = ({ isOpen, onClose }) => {
  const { rreId } = useParams<{ rreId?: string }>();
  const navigate = useNavigate();

  const {
    control,
    reset,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<FormData>({
    defaultValues: {
      tin: null,
      office_code: null,
      tin_office_code_mailing_name: null,
    },
  });

  const [createRreTin, { error, isSuccess }] = useCreateRreTinMutation();

  useEffect(() => {
    if (hasApiError(error)) {
      Object.entries(error.data).forEach(([field, messages]) => {
        setError(field as keyof FormData, {
          type: "manual",
          message: Array.isArray(messages) ? messages[0] : messages,
        });
      });
    }
  }, [error, setError]);

  useEffect(() => {
    if (isSuccess) {
      onClose();
    }
  }, [onClose, isSuccess]);

  const onSubmit: SubmitHandler<FormData> = useCallback(
    async (data) => {
      try {
        const result = await createRreTin({
          data: {
            rre: Number(rreId),
            ...data,
            office_code: Number(data.office_code),
          },
        }).unwrap();

        if (result.id) {
          reset();
          onClose();
          navigate(`/section-111/rres/${rreId}/tins/${result.id}/overview`);
        }
      } catch (err) {
        console.error("Failed to create RRE TIN:", err);
      }
    },
    [createRreTin, rreId, reset, onClose, navigate],
  );

  const handleClose = useCallback(() => {
    reset();
    onClose();
  }, [reset, onClose]);

  const footerContent = useMemo(() => {
    return <Button onClick={handleSubmit(onSubmit)}>Save</Button>;
  }, [handleSubmit, onSubmit]);

  return (
    <KaboodleModal isOpen={isOpen} onClose={handleClose} footer={footerContent}>
      <Card heading="Create RRE TIN">
        <Controller
          control={control}
          name="tin"
          rules={{ required: "TIN is required" }}
          render={({ field }) => (
            <TextInput
              {...field}
              placeholder="TIN"
              label="TIN"
              errors={errors}
              maxLength={9}
            />
          )}
        />
        <Controller
          control={control}
          name="office_code"
          render={({ field }) => (
            <TextInput
              {...field}
              placeholder="Office Code"
              forceToNull={true}
              label="Office Code"
              errors={errors}
              maxLength={9}
            />
          )}
        />
        <Controller
          control={control}
          name="tin_office_code_mailing_name"
          render={({ field }) => (
            <TextInput
              {...field}
              placeholder="Office Code Mailing Name"
              forceToNull={true}
              label="Office Code Mailing Name"
              errors={errors}
              maxLength={70}
            />
          )}
        />
      </Card>
    </KaboodleModal>
  );
};

export default CreateRRETinModal;
